import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { toast } from "sonner";
import { ActionLink, ActionsCell, ConfirmDeleteDialog, EmptyState, SearchBox, SectionCard, StatusPill, useAccountingPermissions } from "./shared";
import { NEW_LEDGER_OPTION, vendorBlank } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import VendorForm from "./VendorForm";

export default function VendorsMasterPage() {
  const [vendors, setVendors] = useState([]);
  const [vendorLedgers, setVendorLedgers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [form, setForm] = useState(vendorBlank);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const perms = useAccountingPermissions();
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const [vendorsRes, ledgersRes] = await Promise.all([
        api.get("/society/accounting/engine/vendors?includeInactive=true").catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
      ]);
      setVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : []);
      setVendorLedgers((Array.isArray(ledgersRes.data) ? ledgersRes.data : []).filter((l) => l.ledgerGroup === "vendor"));
    } catch (err) {
      toast.error(formatErr(err, "Could not load vendors"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const linkedLedgerIds = useMemo(() => new Set(vendors.filter((v) => v.active !== false).map((v) => v.linkedLedgerId)), [vendors]);
  const availableLedgers = useMemo(() => vendorLedgers.filter((l) => !linkedLedgerIds.has(l.id)), [vendorLedgers, linkedLedgerIds]);

  const filtered = useMemo(() => vendors.filter((v) => {
    if (statusFilter === "active" && v.active === false) return false;
    if (statusFilter === "inactive" && v.active !== false) return false;
    const q = search.trim().toLowerCase();
    if (q && !String(v.vendorName || "").toLowerCase().includes(q) && !String(v.gstin || "").toLowerCase().includes(q)) return false;
    return true;
  }), [vendors, search, statusFilter]);

  const toggleActive = async (vendor) => {
    try {
      await api.patch(`/society/accounting/engine/vendors/${vendor.id}`, { ...vendor, active: !(vendor.active !== false) });
      toast.success(vendor.active === false ? "Vendor reactivated" : "Vendor deactivated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update vendor status"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/society/accounting/engine/vendors/${deleteTarget.id}`);
      toast.success("Vendor deleted");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "This vendor cannot be deleted because accounting records are linked to it. Deactivate it instead."));
    } finally {
      setDeleting(false);
    }
  };

  const createVendor = async () => {
    if (!form.vendorName.trim()) { toast.error("Vendor name is required"); return; }
    try {
      setBusy(true);
      let linkedLedgerId = form.linkedLedgerId;
      const openingBalance = form.openingBalance === "" ? 0 : Number(form.openingBalance);
      if (linkedLedgerId === NEW_LEDGER_OPTION) {
        const ledgerName = (form.newLedgerName || `${form.vendorName} payable`).trim();
        const ledgerRes = await api.post("/society/accounting/engine/ledgers", {
          name: ledgerName, ledgerGroup: "vendor", openingBalance, openingSide: form.openingBalanceSide,
          openingAsOfDate: form.openingAsOfDate,
        });
        linkedLedgerId = ledgerRes.data.id;
      } else if (!linkedLedgerId) {
        toast.error("Choose a linked ledger, or create a new one");
        setBusy(false);
        return;
      }
      await api.post("/society/accounting/engine/vendors", { ...form, linkedLedgerId, openingBalance });
      toast.success("Vendor created");
      setForm(vendorBlank);
      setShowAddForm(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create vendor"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-extrabold text-[#111827]">Vendors</h2>
          <p className="mt-1 text-sm text-[#6B7280]">Vendor master used across Purchase, Payment and Journal vouchers. Each vendor points at its own payable ledger.</p>
        </div>
        {!showAddForm && perms.canCreate && (
          <button type="button" onClick={() => setShowAddForm(true)} className="rounded-sm bg-[#111827] px-4 py-2 text-sm font-semibold text-white hover:bg-[#374151]">
            + Add vendor
          </button>
        )}
      </div>

      {showAddForm && (
        <SectionCard title="Add vendor">
          <VendorForm form={form} setForm={setForm} availableLedgers={availableLedgers} ledgerLocked={false} busy={busy} onSubmit={createVendor}
            onCancel={() => { setShowAddForm(false); setForm(vendorBlank); }} submitLabel="Add vendor" isEdit={false} />
        </SectionCard>
      )}

      <SectionCard>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SearchBox value={search} onChange={setSearch} placeholder="Search name or GSTIN..." />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState>No vendors match this filter. Add one above.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.12em] text-[#6B7280]">
                  <th className="py-2 pr-3">Vendor</th>
                  <th className="py-2 pr-3">Contact</th>
                  <th className="py-2 pr-3">GSTIN</th>
                  <th className="py-2 pr-3">Linked ledger</th>
                  <th className="py-2 pr-3">Outstanding</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((vendor) => (
                  <tr key={vendor.id} className="border-b border-[#F3F4F6]">
                    <td className="py-2 pr-3 font-semibold text-[#111827]">{vendor.vendorName}</td>
                    <td className="py-2 pr-3">{vendor.contactPerson || vendor.mobile || "-"}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{vendor.gstin || "-"}</td>
                    <td className="py-2 pr-3">{vendor.linkedLedgerName || "-"}</td>
                    <td className="py-2 pr-3 text-[#6B7280]">{vendor.outstanding === null || vendor.outstanding === undefined ? "Not available yet" : `₹ ${Number(vendor.outstanding).toLocaleString()}`}</td>
                    <td className="py-2 pr-3"><StatusPill active={vendor.active !== false} /></td>
                    <td className="py-2 pr-3">
                      <ActionsCell>
                        <ActionLink to={`${ACCOUNTING_BASE}/vendors/${vendor.id}`}>View</ActionLink>
                        {perms.canCreate && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink to={`${ACCOUNTING_BASE}/vendors/${vendor.id}/edit`}>Edit</ActionLink>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink tone={vendor.active === false ? "default" : "danger"} onClick={() => toggleActive(vendor)}>
                              {vendor.active === false ? "Reactivate" : "Deactivate"}
                            </ActionLink>
                          </>
                        )}
                        {vendor.canDelete && perms.canManageMasters && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink tone="danger" onClick={() => setDeleteTarget(vendor)}>Delete</ActionLink>
                          </>
                        )}
                      </ActionsCell>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete vendor?"
        identity={deleteTarget?.vendorName}
        safeMessage="This vendor has no accounting transactions and can be permanently deleted."
        busy={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
